/* eslint-disable @typescript-eslint/restrict-plus-operands */
import * as Sentry from '@sentry/react';

export interface SelectedTextContext {
  selectedText: string;
  beforeContext: string;
  afterContext: string;
  fullContext: string;
}

const MIN_SELECTED_TEXT_LENGTH = 7;
const MAX_SELECTED_TEXT_LENGTH = 450;
const MIN_WORDS_IN_SENTENCE = 8;

/**
 * Extracts selected text with surrounding context from an HTML element using a Range
 * @param rootNode The root HTML element containing the selection
 * @param range The Range object representing the selection
 * @returns TextContext object with selection and sentence-aligned context, or null if invalid
 */
export const extractTextWithContext = (rootNode: HTMLElement, range: Range): SelectedTextContext | null => {
  if (!rootNode || !range) {
    return null;
  }

  try {
    const fullText = getFullText(rootNode);
    const selectedText = getSelectedText(range);

    if (
      !selectedText ||
      selectedText.length < MIN_SELECTED_TEXT_LENGTH ||
      selectedText.length > MAX_SELECTED_TEXT_LENGTH
    ) {
      return null;
    }

    const selectionStart = getSelectionStart(rootNode, range);
    const selectionEnd = selectionStart + selectedText.length;

    const beforeContext = getBeforeContext(fullText, selectionStart);
    const afterContext = getAfterContext(fullText, selectionEnd);
    const fullContext = fullText.slice(selectionStart - beforeContext.length, selectionEnd + afterContext.length) ?? '';

    return {
      selectedText,
      beforeContext,
      afterContext,
      fullContext,
    };
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

/**
 * Finds the before context, ensuring at least one sentence has 6+ words
 * @param fullText The full text content
 * @param selectionStart The start position of the selection
 * @returns The before context string
 */
const getBeforeContext = (fullText: string, selectionStart: number): string => {
  const sentenceEndings = /[!.?]\s+/g;
  const beforeMatches = [...fullText.slice(0, selectionStart).matchAll(sentenceEndings)];

  if (beforeMatches.length === 0) {
    return fullText.slice(0, selectionStart) ?? '';
  }

  let matchIndex = beforeMatches.length - 1;

  const currentMatch = beforeMatches[matchIndex];
  if (!currentMatch?.index) {
    return '';
  }

  let beforeStart = currentMatch ? currentMatch.index + currentMatch[0].length : 0;
  let beforeContext = fullText.slice(beforeStart, selectionStart) ?? '';

  // Ensure true sentence start (capital letter)
  while (!/^[A-Z]/.test(beforeContext.trim()) && matchIndex > 0) {
    matchIndex--;
    const match = beforeMatches[matchIndex];
    if (match && match.index) {
      beforeStart = match.index + match[0].length;
      beforeContext = fullText.slice(beforeStart, selectionStart) ?? '';
    } else {
      break;
    }
  }

  let wordCount = (beforeContext.trim().split(/\s+/) ?? []).length;
  while (wordCount < MIN_WORDS_IN_SENTENCE && matchIndex > 0) {
    matchIndex--;
    const match = beforeMatches[matchIndex];
    if (match && match.index) {
      const newBeforeStart = match.index + match[0].length;
      const extendedContext = fullText.slice(newBeforeStart, selectionStart) ?? '';
      if (/^[A-Z]/.test(extendedContext.trim())) {
        beforeStart = newBeforeStart;
        beforeContext = extendedContext;
        wordCount = (beforeContext.trim().split(/\s+/) ?? []).length;
      } else {
        break;
      }
    } else {
      break; // Exit if no valid match
    }
  }

  return beforeContext;
};

/**
 * Finds the after context, ensuring at least one sentence has 6+ words
 * @param fullText The full text content
 * @param selectionEnd The end position of the selection
 * @returns The after context string
 */
const getAfterContext = (fullText: string, selectionEnd: number): string => {
  const sentenceEndings = /[!.?]\s+/g;
  const afterMatches = [...fullText.slice(selectionEnd).matchAll(sentenceEndings)];

  if (afterMatches.length === 0) {
    return fullText.slice(selectionEnd) ?? '';
  }

  let matchIndex = 0;

  const currentMatch = afterMatches[matchIndex];
  if (!currentMatch?.index) {
    return '';
  }

  let afterEnd = currentMatch ? selectionEnd + currentMatch.index + currentMatch[0].length : fullText.length;
  let afterContext = fullText.slice(selectionEnd, afterEnd) ?? '';

  let wordCount = (afterContext.trim().split(/\s+/) ?? []).length;
  while (wordCount < MIN_WORDS_IN_SENTENCE && matchIndex < afterMatches.length - 1) {
    matchIndex++;
    const match = afterMatches[matchIndex];
    if (match && match.index) {
      const newAfterEnd = selectionEnd + match.index + match[0].length;
      afterContext = fullText.slice(selectionEnd, newAfterEnd) ?? '';
      wordCount = (afterContext.trim().split(/\s+/) ?? []).length;
      if (wordCount >= MIN_WORDS_IN_SENTENCE) {
        afterEnd = newAfterEnd;
        break;
      }
    } else {
      break;
    }
  }

  return afterContext;
};

/**
 * Gets the full text content from an HTMLElement, handling null/undefined
 * @param rootNode The root HTML element
 * @returns The text content or empty string if null/undefined
 */
const getFullText = (rootNode: HTMLElement): string => rootNode.textContent ?? '';

/**
 * Extracts the selected text from a Range, handling null/undefined
 * @param range The Range object representing the selection
 * @returns The selected text or empty string if null/undefined
 */
const getSelectedText = (range: Range): string => range.toString() ?? '';

/**
 * Calculates the start position of the selection in the full text
 * @param rootNode The root HTML element
 * @param range The Range object
 * @returns The start position of the selection
 */
const getSelectionStart = (rootNode: HTMLElement, range: Range): number => {
  const preRange = document.createRange();
  preRange.setStart(rootNode, 0);
  preRange.setEnd(range.startContainer, range.startOffset);
  return (preRange.toString() ?? '').length;
};
